'use client';

/**
 * SentryInit — initialises @sentry/browser on first client-side mount.
 *
 * Why a client component (and not @sentry/nextjs): the landing site
 * is a STATIC export (`output: 'export'` in next.config.ts). The
 * `@sentry/nextjs` integration relies on Next's runtime instrumentation
 * which doesn't work in static export. `@sentry/browser` initialised
 * in a client-side useEffect works fine for what we actually need
 * (browser-side error capture).
 *
 * Render returns null — no DOM. The component exists only to run
 * Sentry.init() exactly once per page lifetime.
 *
 * Configuration:
 *   NEXT_PUBLIC_SENTRY_DSN  — embedded in the bundle at build time
 *   NEXT_PUBLIC_SENTRY_ENVIRONMENT  — 'production' / 'staging' (optional)
 *
 * When NEXT_PUBLIC_SENTRY_DSN is unset, Sentry.init is skipped — clean
 * dev with no error reports going anywhere.
 */

import { useEffect } from 'react';

const DSN = process.env.NEXT_PUBLIC_SENTRY_DSN ?? '';
const ENV = process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT ?? 'production';
// Audit B1 (2026-05-04): tag every event with the build SHA so Sentry's
// release-bucketing works. The Sentry sourcemap-upload workflow uploads
// maps under the same tag (short git SHA), so unminification matches.
// `next build` substitutes process.env.NEXT_PUBLIC_RELEASE at build time;
// missing var → 'unknown' (better than no release tag at all).
const RELEASE = process.env.NEXT_PUBLIC_RELEASE ?? 'unknown';

export function SentryInit(): null {
  useEffect(() => {
    if (!DSN) return;
    let cancelled = false;
    (async () => {
      try {
        const Sentry = await import('@sentry/browser');
        if (cancelled) return;
        Sentry.init({
          dsn: DSN,
          environment: ENV,
          release: RELEASE,
          // 5% transaction sample rate — enough for trend visibility,
          // small enough to stay under the free tier's 10k/month.
          tracesSampleRate: 0.05,
          // Don't auto-capture user data. Photos, emails, free-text
          // form values must NEVER be sent to Sentry.
          sendDefaultPii: false,
          // Strip query strings from URL events (which often contain
          // single-use magic-link tokens, scan IDs, etc.) and refuse
          // to forward DOM breadcrumbs that contain user-typed text.
          beforeSend(event) {
            // Drop ?token=... / ?session=... query strings everywhere.
            if (event.request?.url) {
              event.request.url = event.request.url.split('?')[0];
            }
            // Strip any user-controlled attributes that may contain PII.
            if (event.request?.headers) {
              delete event.request.headers['Authorization'];
              delete event.request.headers['authorization'];
              delete event.request.headers['Cookie'];
              delete event.request.headers['cookie'];
            }
            return event;
          },
          beforeBreadcrumb(breadcrumb) {
            // Drop console + xhr breadcrumbs entirely — too easy for
            // them to capture user-typed text or photo data URIs.
            if (breadcrumb.category === 'console') return null;
            if (breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') {
              if (breadcrumb.data?.url) {
                breadcrumb.data.url = String(breadcrumb.data.url).split('?')[0];
              }
              // Strip request/response bodies — fetch breadcrumbs sometimes
              // include them.
              delete breadcrumb.data?.request_body_size;
              delete breadcrumb.data?.response_body_size;
            }
            return breadcrumb;
          },
        });
      } catch (err) {
        // Sentry init failure must NEVER break the page. Log only.
        console.warn('[sentry] init failed:', (err as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return null;
}
